import {
  Children,
  cloneElement,
  type PropsWithChildren,
  type ReactElement,
} from "react";

export const Screens = {
  hotelsList: "hotels_list",
  hotelsReviewFullInfo: "hotels_review_full_info",
  hotelsBookingConfirmation: "hotels_booking_confirmation",
} as const;

export const ComponentType = {
  button: "button",
  link: "link",
} as const;

export const EntityContext = {
  flightsTogglePriceWatch: "flights_toggle_price_watch",
  flightsDisruptionProtectionDetails: "flights_disruption_protection_details",
  flightsAddPassenger: "flights_add_passenger",
  flightsBestPriceGuaranteeDetails: "flights_best_price_guarantee_details",
  flightsEarnMoreDetails: "flight_earn_more_details",
  addGuest: "add_guest",
} as const;

export type MiniAppAnalyticsScreen = (typeof Screens)[keyof typeof Screens];

export type MiniAppAnalyticsEntityComponent =
  (typeof ComponentType)[keyof typeof ComponentType];

export type MiniAppAnalyticsEntityContext =
  (typeof EntityContext)[keyof typeof EntityContext];

type MiniAppAnalyticsBase = {
  currentScreen: MiniAppAnalyticsScreen;
};

type MiniAppAnalyticsShow = MiniAppAnalyticsBase & {
  event: "show";
};

type MiniAppAnalyticsClick = MiniAppAnalyticsBase & {
  event: "click";
  entityComponent: MiniAppAnalyticsEntityComponent;
  entityContext: MiniAppAnalyticsEntityContext;
};

export type MiniAppAnalyticsAttributes =
  | MiniAppAnalyticsShow
  | MiniAppAnalyticsClick;

export type MiniAppAnalyticsProps =
  PropsWithChildren<MiniAppAnalyticsAttributes>;

const buildMiniAppAnalyticsAttributes = (
  attributes: MiniAppAnalyticsAttributes
) => {
  if (attributes.event === "show") {
    return {
      "data-mini-app-analytics-show": "true",
      "data-mini-app-analytics-current-screen": attributes.currentScreen,
    };
  }

  const { currentScreen, entityComponent, entityContext } =
    attributes as MiniAppAnalyticsClick;

  return {
    "data-mini-app-analytics-click": "true",
    "data-mini-app-analytics-current-screen": currentScreen,
    "data-mini-app-analytics-entity_component": entityComponent,
    "data-mini-app-analytics-entity_context": entityContext,
  };
};

export const MiniAppAnalytics = ({
  children,
  ...attributes
}: MiniAppAnalyticsProps) => {
  const dataAttributes = buildMiniAppAnalyticsAttributes(attributes);

  if (attributes.event === "click") {
    const child = Children.only(children);
    return cloneElement(child as ReactElement, {
      ...dataAttributes,
    });
  }

  return <div {...dataAttributes}>{children}</div>;
};
